import { Component } from '@angular/core';
import { environment } from 'src/environments/environment';

/** Where the badge links to, so a non-production tab is one click from the real thing. */
const PRODUCTION_ORIGIN = 'https://auth.sytex.io';

/**
 * Marks every non-production build so a dev/local tab is never mistaken for the real
 * auth page: a red pill for the state, and a quiet link under it for the way out.
 *
 * Two independent signals, because neither one alone is enough:
 *
 * - WHETHER to show it comes from `environment.name`, baked in at build time from the
 *   `ANGULAR_ENVIRONMENT` variable. Both `production` and `production_eu` set it to
 *   'production', so the badge is absent from every user-facing deploy. Angular's
 *   `isDevMode()` would not work here: dev is built with `-c production` but its
 *   environment sets `production: false`, so `main.ts` never calls `enableProdMode()`
 *   and `isDevMode()` is true on `auth.dev.sytex.io` too.
 * - WHICH label to show comes from the hostname, because `environment.name` cannot
 *   tell the two non-production cases apart: the `development` GitHub environment
 *   also sets it to 'local', so the dev deploy would otherwise announce itself as
 *   LOCAL. The deployed apps are the only ones served from `auth.<env>.sytex.io` —
 *   anything else (`npm start` behind login.dev.sytex.io, localhost) is a machine.
 */
@Component({
  selector: 'app-env-badge',
  templateUrl: './env-badge.component.html',
  styleUrls: ['./env-badge.component.scss']
})
export class EnvBadgeComponent {
  // Not localized on purpose: unlike the link beside it, these are build
  // identifiers, not product copy.
  readonly label: string | null =
    environment.name === 'production'
      ? null
      : window.location.hostname.startsWith('auth.')
        ? 'DEVELOPMENT'
        : 'LOCAL';

  // Same screen on production: the path carries the locale prefix and the route, so
  // /es/login lands on /es/login. The query string is deliberately dropped — `r` is a
  // return URL pointing back at THIS environment, and carrying it over would send a
  // production login back into dev.
  readonly productionUrl = `${PRODUCTION_ORIGIN}${window.location.pathname}`;
}
